import React, { useState, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const SingleSelectSearch = props => {
  const {
    wrapperClassName = "searchboxcomp-wrapper",
    inputClassName = "search-input-text font14 truncate",
    dropdownClassName = "search-dropdown borderbox",
    dropdownItemClassName = "search-dropdown-item",
    placeholder,
    defaultSearchText,
    items,
    itemName = "name",
    filter,
    minSearchLength = 1,
    updateParentState,
    debounceTime = 300 // Time to debounce in ms
  } = props;

  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(defaultSearchText);
  const [lastSelected, setLastSelected] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const prevItems = usePrevious(items);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const node = useRef();

  useEffect(() => {
    document.addEventListener("mousedown", hideDropdownMenu);
    if (searchText.length >= minSearchLength) {
      filterItems.current(searchText, items);
    }
    return () => document.removeEventListener("mousedown", hideDropdownMenu);
  }, []);

  useEffect(() => {
    setSearchText(defaultSearchText);
  }, [defaultSearchText]);

  useEffect(() => {
    if (!isEqual(prevItems, items)) {
      setFilteredItems(items);
    }
  }, [items]);

  useEffect(() => {
    // If the dropdown is hidden and user has changed the last selected item
    // without selecting something, revert the change.
    if (!showResults) {
      if (lastSelected && lastSelected !== searchText) {
        setSearchText(lastSelected);
      }
    } else {
      if (props.scrollToTop) {
        const parentOffset = document.querySelector(props.scrollToTop).offsetTop;
        const childOffset = node.current.parentElement.offsetTop;
        const scrollHeight = childOffset - parentOffset;
        $(props.scrollToTop).animate({ scrollTop: scrollHeight });
      }
    }
  }, [showResults]);

  const setTextAndUpdateParent = item => {
    setShowResults(false);
    filterItemsAndSetSearchText(item[itemName]);
    setLastSelected(item[itemName]);
    updateParentState(item);
  };

  const filterItemsAndSetSearchText = text => {
    setSearchText(text);
    if (text.length >= minSearchLength) {
      filterItems.current(text, items);
    }
  };

  // Needs text and items to filter since useRef captures the initial items
  const filterItems = useRef(
    debounce(
      async (text, items) => {
        setLoading(true);
        const itemFilter = filter || defaultFilter;
        const filteredItems = await itemFilter(text, items);
        setFilteredItems(filteredItems);
        setLoading(false);
      },
      debounceTime,
      { leading: true }
    )
  );

  const defaultFilter = (text, items) => {
    return items.filter(item =>
      item[itemName].toLowerCase().includes(text.toLowerCase())
    );
  };

  const hideDropdownMenu = e => {
    if (!node.current) return;
    if (node.current.contains(e.target)) {
      return;
    } else {
      setShowResults(false);
    }
  };

  const resultsComponent = items => {
    if (loading) {
      return (
        <div className={`${dropdownItemClassName}-unselectable`}>
          {t('Loading results')}...
        </div>
      );
    } else if (!items.length) {
      return (
        <div className={`${dropdownItemClassName}-unselectable`}>
          {t('No results found')}.
        </div>
      );
    } else {
      return items.map(item => (
        <div
          className={`${dropdownItemClassName} \
          ${showResults &&
            !!(item[itemName] == searchText) &&
            "search-dropdown-item-selected"}`}
          key={item[itemName]}
          onClick={() => setTextAndUpdateParent(item)}
        >
          {item[itemName]}
        </div>
      ));
    }
  };

  return (
    <div className={wrapperClassName} ref={node}>
      <input
        type="text"
        className={inputClassName}
        placeholder={placeholder}
        value={searchText}
        onChange={e => filterItemsAndSetSearchText(e.currentTarget.value)}
        onFocus={() => setShowResults(true)}
      />
      {searchText.length >= minSearchLength && showResults && (
        <div className={dropdownClassName}>
          {resultsComponent(filteredItems)}
        </div>
      )}
    </div>
  );
};

export default SingleSelectSearch;
